"use client";

import { m } from "motion/react";
import Image from "next/image";
import Link from "next/link";

import { useUserQuery } from "@trex-2b/hooks";
import { buildTrackerProps, logGAEvent, mergeClasses } from "@trex-2b/libs";
import {
  getStoredPassportInfo,
  LOGIN_STRATEGY_STORAGE_KEY,
} from "@trex-2b/libs/config";
import { DASHBOARD_ROUTES, GROWTH_ROUTES } from "@trex-2b/libs/config/routes";
import { UserAvatar } from "@trex-2b/ui";
import { useLandingLoginGuard } from "@trex-2b/ui/auth/use-landing-login-guard";

/** Returning user if this browser has completed login before (see modal-auth-login). */
function getPortalDestination(): string {
  const isReturningUser =
    typeof window !== "undefined" &&
    Boolean(window.localStorage.getItem(LOGIN_STRATEGY_STORAGE_KEY));
  return isReturningUser
    ? DASHBOARD_ROUTES.CAMPAIGNS
    : GROWTH_ROUTES.ON_BOARDING;
}

function openInNewTab(url: string) {
  window.open(url, "_blank");
}

/**
 * Landing top nav — structure & tokens from Figma MCP:
 * authenticated `8788:11432`, guest `9155:3692`.
 * Bar height 72px (design); CTA buttons 46px, vertically centered.
 * Assets: `/public/images/landing/nav/`.
 */

const NAV_ITEMS = [
  { label: "User Matching", section: "matching" },
  { label: "Why T-Rex", section: "why" },
  { label: "How It Works", section: "how" },
  { label: "Contact Sales", section: "contact" },
] as const;

function navHref(section: (typeof NAV_ITEMS)[number]["section"]) {
  if (section === "contact") {
    return GROWTH_ROUTES.LANDING_PAGE_CONTACT_SALES;
  }
  return `${GROWTH_ROUTES.LANDING_PAGE}?section=${section}`;
}

export type LandingNavBarProps = {
  className?: string;
};

/** Root frame — `8788:11432` / `9155:3692` */
export function LandingNavBar({ className }: LandingNavBarProps) {
  const { isLoggedIn, runWithLogin } = useLandingLoginGuard();
  const { data: passportInfo } = useUserQuery({
    bugsnagApp: "growth-portal",
  });
  const storedPassportInfo = getStoredPassportInfo();
  const nickname =
    passportInfo?.loginSocialAccount ??
    storedPassportInfo?.loginSocialAccount ??
    "";

  return (
    <>
      <header
        className={mergeClasses(
          "sticky top-0 z-50 flex h-[72px] w-full shrink-0 items-center justify-between bg-[#1c1916] pl-[46px] pr-[24px] backdrop-blur-md drop-shadow-[0px_0px_0px_rgba(255,255,255,0.08)]",
          className,
        )}
      >
        {/* `8788:11433` / `9155:3693` PRISM */}
        <div className="relative h-[15.051px] w-[236px] shrink-0">
          <Link
            href={GROWTH_ROUTES.LANDING_PAGE}
            className="absolute inset-0 block max-w-none"
            data-testid="landing-nav-bar-logo-link"
          >
            <Image
              alt="PRISM"
              src={"/images/landing/nav/prism-logo.svg"}
              fill
              className="object-contain object-left"
              sizes="236px"
            />
          </Link>
        </div>

        {/* `8788:11439` / `9155:3699` */}
        <div className="relative flex shrink-0 items-center">
          {/* `8788:11440` / `9155:3700` */}
          <nav
            aria-label="Primary"
            className="flex shrink-0 items-center gap-[50px] font-inter text-[16px] leading-0 font-medium tracking-[-0.3px] not-italic whitespace-nowrap"
          >
            {NAV_ITEMS.map(({ label, section }) => (
              <Link
                key={label}
                href={navHref(section)}
                className="relative flex shrink-0 flex-col justify-center text-white no-underline transition-colors hover:text-[rgba(255,255,255,0.6)]"
                data-testid={`landing-nav-bar-nav-${section}-link`}
              >
                <span className="font-inter leading-[30.8px]">{label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {!isLoggedIn ? (
          <GuestActions
            onSignIn={() => {
              const destination = getPortalDestination();
              runWithLogin(() => openInNewTab(destination));
            }}
            onGetStarted={() =>
              runWithLogin(() => openInNewTab(GROWTH_ROUTES.ON_BOARDING))
            }
          />
        ) : (
          <AuthenticatedActions nickname={nickname} />
        )}
      </header>
    </>
  );
}

/** `9155:3705` */
function GuestActions({
  onSignIn,
  onGetStarted,
}: {
  onSignIn: () => void;
  onGetStarted: () => void;
}) {
  return (
    <div className="relative flex shrink-0 items-center gap-[13px]">
      {/* `9155:3706` Button / On Light */}
      <m.button
        type="button"
        onClick={onSignIn}
        className="font-inter relative flex h-[46px] shrink-0 cursor-pointer items-center overflow-clip rounded-[100px] px-[21px] py-[14px] shadow-[0px_0px_2px_0px_rgba(0,0,0,0.1),0px_1px_8px_0px_rgba(0,0,0,0.1)] outline-none"
        whileTap={{ scale: 0.8 }}
        transition={{ type: "spring", stiffness: 520, damping: 18 }}
        data-testid="landing-nav-bar-sign-in-btn"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[100px]"
        >
          <span className="absolute inset-0 rounded-[100px] bg-[rgba(140,140,140,0.25)]" />
          <span className="absolute inset-0 rounded-[100px] bg-[#171717] mix-blend-plus-lighter backdrop-blur-[6px]" />
        </span>
        {/* `9155:3707` */}
        <div className="relative flex shrink-0 flex-col justify-center leading-0 font-semibold not-italic">
          <p className="font-inter leading-[30.8px] text-[16px] tracking-[-0.3px] whitespace-nowrap text-white">
            Sign in
          </p>
        </div>
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_3px_3px_0.5px_-3.5px_white,inset_2px_2px_0.5px_-2px_#262626,inset_-2px_-2px_0.5px_-2px_#262626,inset_0px_0px_0px_1px_#a6a6a6,inset_0px_0px_8px_0px_#f2f2f2]"
        />
      </m.button>

      {/* `9155:3708` Button / On Dark */}
      <m.button
        type="button"
        onClick={onGetStarted}
        className="font-inter relative flex h-[46px] w-[177px] shrink-0 cursor-pointer items-center gap-[10.783px] overflow-clip rounded-[134px] px-[28.304px] py-[12px] shadow-[0px_0px_2.696px_0px_rgba(0,0,0,0.1),0px_1.348px_10.783px_0px_rgba(0,0,0,0.1)] outline-none"
        whileTap={{ scale: 0.8 }}
        transition={{ type: "spring", stiffness: 520, damping: 18 }}
        data-testid="landing-nav-bar-get-started-btn"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[134px] backdrop-blur-[8.087px]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.82) 100%)",
          }}
        />
        {/* `9155:3709` — MCP exports leading space inside label */}
        <div className="relative flex shrink-0 flex-col justify-center leading-0 font-semibold not-italic">
          <p className="font-inter leading-[30.8px] text-[16px] tracking-[-0.3px] whitespace-nowrap text-[#1c1916]">{` Get Started`}</p>
        </div>
        {/* `9155:3710` */}
        <div className="relative size-[42px] shrink-0">
          <Image
            alt=""
            src="/images/landing/nav/get-started-arrow.svg"
            fill
            className="object-contain"
            sizes="42px"
          />
        </div>
      </m.button>
    </div>
  );
}

/** `8788:11445` — authenticated CTA cluster */
function AuthenticatedActions({ nickname }: { nickname: unknown }) {
  return (
    <div className="relative flex shrink-0 items-center">
      {/* `8788:11446` Button / On Dark */}
      <m.button
        type="button"
        whileTap={{ scale: 0.8 }}
        transition={{ type: "spring", stiffness: 520, damping: 18 }}
        className="font-inter relative flex h-[46px] w-[154px] shrink-0 cursor-pointer items-center gap-[10.783px] overflow-clip rounded-[134px] px-[8px] py-[12px] shadow-[0px_0px_2.696px_0px_rgba(0,0,0,0.1),0px_1.348px_10.783px_0px_rgba(0,0,0,0.1)] outline-none"
        data-testid="landing-nav-bar-enter-portal-btn"
        onClick={() => {
          logGAEvent("cta_click", {
            cta_id: "landing_nav_enter_portal",
            cta_text: "Enter Portal",
            target_route: DASHBOARD_ROUTES.CAMPAIGNS,
          });
          openInNewTab(DASHBOARD_ROUTES.CAMPAIGNS);
        }}
        {...buildTrackerProps()}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[134px] backdrop-blur-[8.087px]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.82) 100%)",
          }}
        />
        {/* `8788:11447` */}
        <div className="relative flex shrink-0 items-center justify-center">
          {/* `8788:11448` Avatar */}
          <div className="relative size-[34px] shrink-0 overflow-clip rounded-[9443.5px]">
            <UserAvatar
              nickname={nickname}
              size={34}
              alt="Portal user avatar"
            />
          </div>
        </div>
        {/* `8788:11449` */}
        <div className="relative flex shrink-0 flex-col justify-center leading-0 font-semibold not-italic">
          <p className="font-inter leading-[30.8px] text-[16px] tracking-[-0.3px] whitespace-nowrap text-[#1c1916]">
            Enter Portal
          </p>
        </div>
      </m.button>
    </div>
  );
}
